import { zodResolver } from '@hookform/resolvers/zod'
import { Typography } from '@material-ui/core'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import Button from '@/components/button'
import InputText from '@/components/input-text'
import { useAuth } from '@/hooks/use-auth'
import { recoveryUserPassword } from '@/services/recovery-user-password'

import { useStyles } from './styles'

interface FormValues {
  newPassword: string
  newPasswordConfirmation: string
}

interface NewPasswordProps {
  handleClose: () => void
}

const formSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'A senha deve ter no mínimo 8 caracteres')
      .max(20, 'A senha deve ter no máximo 20 caracteres'),
    newPasswordConfirmation: z.string().min(0, 'O campo é obrigatório'),
  })
  .refine((data) => data.newPassword === data.newPasswordConfirmation, {
    message: 'As senhas não conferem',
    path: ['newPasswordConfirmation'],
  })

export default function NewPassword({ handleClose }: NewPasswordProps) {
  const classes = useStyles()
  const { user } = useAuth()

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    getValues,
    setError,
  } = useForm({
    defaultValues: {
      newPassword: '',
      newPasswordConfirmation: '',
    },
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  })

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      await recoveryUserPassword(user.id, data.newPassword)
    } catch (err: any) {
      reset()
      setError(
        'newPassword',
        {
          message: 'Houve um erro, tente mais tarde',
        },
        { shouldFocus: true },
      )
    }
  }

  if (!isSubmitSuccessful)
    return (
      <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <Typography>Para continuar, insira sua nova senha:</Typography>

        <InputText
          className={classes.input}
          id='new-password-input'
          label={'Nova senha'}
          type={'password'}
          success={!!getValues('newPassword') && !errors.newPassword}
          error={!!errors.newPassword}
          helperText={errors.newPassword?.message}
          {...register('newPassword')}
        />

        <InputText
          className={classes.input}
          id='password-confirmation-input'
          label={'Confirme a sua nova senha'}
          type={'password'}
          success={
            !!getValues('newPasswordConfirmation') &&
            !errors.newPasswordConfirmation
          }
          error={!!errors.newPasswordConfirmation}
          helperText={errors.newPasswordConfirmation?.message}
          {...register('newPasswordConfirmation')}
        />

        <div className={classes.buttons}>
          <Button onClick={handleClose} outlined>
            Cancelar
          </Button>
          <Button
            disabled={Boolean(
              errors.newPassword ||
                errors.newPasswordConfirmation ||
                isSubmitting,
            )}
            loading={isSubmitting}
            type={'submit'}
          >
            Continuar
          </Button>
        </div>
      </form>
    )

  return (
    <div className={classes.passwordChanged}>
      <Typography>Sua senha foi alterada.</Typography>
      <Button onClick={handleClose} outlined>
        Fechar
      </Button>
    </div>
  )
}
